/*
| Search, without a search endpoint.
|
| The catalog is small and already cached for every screen, so the index is
| built in the browser and a keystroke costs no request. Hex strings are
| matched by shape: a 20-byte one is a wallet; a 32-byte one could be a
| transaction hash or a 32-byte wallet, so both are offered.
*/

import { builderRef } from '../models/builders.js';
import { marketHref, modeLabel } from './catalog.js';

const ADDRESS = /^0x[0-9a-f]{40}$/i;
const HASH = /^0x[0-9a-f]{64}$/i;

const lower = (values) => values.filter(Boolean).map((value) => String(value).toLowerCase());

export function buildIndex({ pairs = [], builders = [], tokens = [] } = {}) {
  const markets = (pairs ?? []).map((pair) => ({
    type: 'market',
    label: pair.symbol,
    subtitle: [modeLabel(pair.marketType), pair.category].filter(Boolean).join(' · '),
    href: marketHref(pairs, pair),
    terms: lower([pair.symbol, pair.baseToken, pair.baseAssetName]),
  }));

  const apps = (builders ?? [])
    .map((builder) => ({
      type: 'builder',
      label: builder.name,
      subtitle: builder.kind === 'group' ? 'Builder group' : `Builder${builder.code ? ` · ${builder.code}` : ''}`,
      href: builderRef(builder).href,
      terms: lower([builder.name, builder.code, builder.id, ...(builder.applications ?? []).map((app) => app.clientId)]),
    }))
    .filter((entry) => entry.href);

  const assets = (tokens ?? []).map((token) => ({
    type: 'token',
    label: token.symbol ?? token.contractAddress,
    subtitle: token.name ?? 'Unlisted token',
    href: `/tokens/${encodeURIComponent(token.symbol ?? token.contractAddress)}`,
    terms: lower([token.symbol, token.name, token.contractAddress]),
  }));

  return [...markets, ...apps, ...assets];
}

function score(entry, query) {
  const label = entry.label.toLowerCase();
  if (label.startsWith(query)) return 0;
  if (entry.terms.some((term) => term.startsWith(query))) return 1;
  if (entry.terms.some((term) => term.includes(query))) return 2;
  return null;
}

export function searchIndex(index, raw, limit = 8) {
  const query = String(raw ?? '').trim().toLowerCase();
  if (!query) return [];

  return index
    .map((entry) => ({ entry, rank: score(entry, query) }))
    .filter(({ rank }) => rank !== null)
    .sort((a, b) => a.rank - b.rank || a.entry.label.localeCompare(b.entry.label) || a.entry.subtitle.localeCompare(b.entry.subtitle))
    .slice(0, limit)
    .map(({ entry }) => entry);
}

/** Hits implied by the query's shape alone. `links` builds the hrefs. */
export function literalHits(raw, links) {
  const query = String(raw ?? '').trim();

  if (ADDRESS.test(query)) {
    return [{ type: 'address', label: query, subtitle: 'Wallet', href: links.address(query), external: links.external ?? true }];
  }

  if (HASH.test(query)) {
    return [
      { type: 'transaction', label: query, subtitle: 'Transaction on the Sei explorer', href: links.transaction(query), external: true },
      { type: 'address', label: query, subtitle: 'Wallet', href: links.address(query), external: links.external ?? true },
    ];
  }

  return [];
}

/**
 * Loads the search index once, keeping only the parts that loaded. A failed
 * builder read is retried, but only after the server's Retry-After horizon (a
 * minute when it gave none), never on every keystroke.
 */
export function createIndexLoader({ loadPairs, loadBuilders, loadTokens = async () => [], now = Date.now }) {
  let pairs = null;
  const optional = {
    builders: { load: loadBuilders, value: null, retryAt: 0 },
    tokens: { load: loadTokens, value: null, retryAt: 0 },
  };

  async function fill(part) {
    if (part.value !== null || now() < part.retryAt) return;
    try {
      part.value = await part.load();
    } catch (error) {
      const seconds = Number.isFinite(error?.retryAfter) ? error.retryAfter : 60;
      part.retryAt = now() + seconds * 1000;
    }
  }

  return {
    async load() {
      if (pairs === null) pairs = await loadPairs();
      await Promise.all([fill(optional.builders), fill(optional.tokens)]);
      return buildIndex({ pairs, builders: optional.builders.value ?? [], tokens: optional.tokens.value ?? [] });
    },
  };
}

/** Index matches first, then hits implied by the query's shape, each href once. */
export function allHits(index, raw, links, limit = 8) {
  const seen = new Set();
  return [...searchIndex(index, raw, limit), ...literalHits(raw, links)].filter((hit) => {
    if (!hit.href || seen.has(hit.href)) return false;
    seen.add(hit.href);
    return true;
  });
}
